import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { type ReactNode } from 'react';

/**
 * Above the mobile tab bar (1250); below card/scan sheets (1400+).
 * Keep route-modals in the React tree (do not portal to `document.body`) so
 * app providers like ModalProvider stay reachable — including across HMR.
 */
export const FULL_SCREEN_ROUTE_MODAL_Z = 1300;

const modalTransition = {
  duration: 0.32,
  ease: [0.32, 0.72, 0, 1] as const,
};

type FullScreenRouteModalProps = {
  open: boolean;
  /** Accessible name for the dialog. */
  label: string;
  children: ReactNode;
};

/**
 * Native-style fullScreen modal shell: vertical cover over a kept underlay route.
 * Mount as a sibling of the underlay (still under app providers), not via body portal.
 */
export function FullScreenRouteModal({ open, label, children }: FullScreenRouteModalProps) {
  const prefersReducedMotion = useReducedMotion();

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key={`route-modal-${label}`}
          role="dialog"
          aria-modal="true"
          aria-label={label}
          initial={prefersReducedMotion ? { opacity: 0 } : { y: '100%' }}
          animate={prefersReducedMotion ? { opacity: 1 } : { y: 0 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { y: '100%' }}
          transition={modalTransition}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: FULL_SCREEN_ROUTE_MODAL_Z,
            display: 'flex',
            flexDirection: 'column',
            background: '#000',
          }}
        >
          {children}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
